import type { Body, BodyId } from './types';

/**
 * Fixed reference-data constants for all nine bodies (AD-1). This is the single
 * source of body data; it is not user-editable.
 *
 * Frame invariant: the analytic Kepler solver (later stories) returns positions
 * in a focus-centred SI frame, origin at the current central body's focus. No
 * downstream consumer re-offsets by c = a*e or re-centres. Story 1.1 does not
 * animate, but the invariant is recorded here for Story 1.2.
 *
 * Units (AD-5): SI throughout. `a` is in metres, `e` is dimensionless, `mu` is in
 * m^3 s^-2, `collisionRadius` and `physicalRadius` are in metres. No AU, day, or
 * degree literal is used as a stored value; the AU reference figures below are
 * converted to metres once, here, via AU_IN_METRES. `physicalRadius` (true
 * equatorial radius, Story 3.3) is kept DISTINCT from `collisionRadius` (AD-1):
 * both hold the true radius today, but Epic 4 may adjust collision radii without
 * corrupting the realistic display sizes that read `physicalRadius`.
 */

/** One astronomical unit in SI metres. The single reference conversion factor. */
export const AU_IN_METRES = 1.495978707e11;

/** Reference semi-major axes, in astronomical units, converted to metres below. */
const SEMI_MAJOR_AXIS_AU: Record<Exclude<BodyId, 'sun'>, number> = {
  mercury: 0.387,
  venus: 0.723,
  earth: 1.0,
  mars: 1.524,
  jupiter: 5.203,
  saturn: 9.537,
  uranus: 19.191,
  neptune: 30.07
};

const au = (astronomicalUnits: number): number => astronomicalUnits * AU_IN_METRES;

/**
 * The nine reference bodies in canonical order (Sun first, then planets by orbit).
 * mu and collisionRadius carry real SI reference values; Story 1.1 does not
 * exercise them numerically (no animation, no collisions), but they are typed and
 * plausible so later stories inherit correct data.
 */
export const BODIES: readonly Body[] = [
  {
    id: 'sun',
    name: 'Sun',
    colour: '#ffd700',
    // The Sun is central by default and does not orbit; a/e are typed placeholders
    // and only become live if the Sun later becomes an orbiting body (Story 2.2).
    a: 0,
    e: 0,
    mu: 1.32712440018e20,
    collisionRadius: 6.957e8,
    physicalRadius: 6.957e8
  },
  {
    id: 'mercury',
    name: 'Mercury',
    colour: '#a5a5a5',
    a: au(SEMI_MAJOR_AXIS_AU.mercury),
    e: 0.2056,
    mu: 2.2032e13,
    collisionRadius: 2.4397e6,
    physicalRadius: 2.4397e6
  },
  {
    id: 'venus',
    name: 'Venus',
    colour: '#ff7f7f',
    a: au(SEMI_MAJOR_AXIS_AU.venus),
    e: 0.0068,
    mu: 3.24859e14,
    collisionRadius: 6.0518e6,
    physicalRadius: 6.0518e6
  },
  {
    id: 'earth',
    name: 'Earth',
    colour: '#4a90e2',
    a: au(SEMI_MAJOR_AXIS_AU.earth),
    e: 0.0167,
    mu: 3.986004418e14,
    collisionRadius: 6.371e6,
    physicalRadius: 6.371e6
  },
  {
    id: 'mars',
    name: 'Mars',
    colour: '#c0392b',
    a: au(SEMI_MAJOR_AXIS_AU.mars),
    e: 0.0934,
    mu: 4.282837e13,
    collisionRadius: 3.3895e6,
    physicalRadius: 3.3895e6
  },
  {
    id: 'jupiter',
    name: 'Jupiter',
    colour: '#e67e22',
    a: au(SEMI_MAJOR_AXIS_AU.jupiter),
    e: 0.0489,
    mu: 1.26686534e17,
    collisionRadius: 6.9911e7,
    physicalRadius: 6.9911e7
  },
  {
    id: 'saturn',
    name: 'Saturn',
    colour: '#f1c40f',
    a: au(SEMI_MAJOR_AXIS_AU.saturn),
    e: 0.0565,
    mu: 3.7931187e16,
    collisionRadius: 5.8232e7,
    physicalRadius: 5.8232e7
  },
  {
    id: 'uranus',
    name: 'Uranus',
    colour: '#7fb3d5',
    a: au(SEMI_MAJOR_AXIS_AU.uranus),
    e: 0.0457,
    mu: 5.793939e15,
    collisionRadius: 2.5362e7,
    physicalRadius: 2.5362e7
  },
  {
    id: 'neptune',
    name: 'Neptune',
    colour: '#2980b9',
    a: au(SEMI_MAJOR_AXIS_AU.neptune),
    e: 0.0086,
    mu: 6.836529e15,
    collisionRadius: 2.4622e7,
    physicalRadius: 2.4622e7
  }
] as const;

/** Canonical body ids, in canonical order. */
export const BODY_IDS: readonly BodyId[] = BODIES.map((b) => b.id);

/** The default central body of the system. */
export const DEFAULT_CENTRAL_BODY: BodyId = 'sun';

/** The default selected (edit-focus) body of the system. */
export const DEFAULT_SELECTED_BODY: BodyId = 'earth';

/** Look up a body's fixed reference data by id. */
export function getBody(id: BodyId): Body {
  const body = BODIES.find((b) => b.id === id);
  if (!body) {
    throw new Error(`Unknown body id: ${id}`);
  }
  return body;
}
